import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getById, patchById } from '../../context/users/UserActions'
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaUser, 
  FaEnvelope, 
  FaGlobe, 
  FaSave, 
  FaUserEdit
} from 'react-icons/fa'
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap'
import './UpdateOwnInfo.css'

// eslint-disable-next-line
export default function (props) {
  const EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/
  const navigate = useNavigate()
  const { authenticatedUser, setAlert } = useUserContext()

  const [userEmail, setEmail] = useState('')
  const [validEmail, setValidEmail] = useState(true)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [country, setCountry] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  
  const handleChangeEmail = (e) => {
    setEmail(e.target.value)
    if(e.target.value === '') {
      setValidEmail(true)
      setErrors(prev => ({ ...prev, email: '' }))
    } else {
      const isValid = EMAIL_REGEX.test(e.target.value)
      setValidEmail(isValid)
      setErrors(prev => ({ 
        ...prev, 
        email: isValid ? '' : 'Correo con formato inválido' 
      }))
    }
  }

  const handleChangeFirstName = (e) => {
    setFirstName(e.target.value)
    setErrors(prev => ({ 
      ...prev, 
      firstName: e.target.value.trim() === '' ? 'El nombre es requerido' : '' 
    }))
  }

  const handleChangeLastName = (e) => {
    setLastName(e.target.value)
    setErrors(prev => ({ 
      ...prev, 
      lastName: e.target.value.trim() === '' ? 'Los apellidos son requeridos' : '' 
    }))
  }

  const handleChangeCountry = (e) => {
    setCountry(e.target.value)
    setErrors(prev => ({ 
      ...prev, 
      country: e.target.value.trim() === '' ? 'El país es requerido' : '' 
    }))
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!userEmail.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!validEmail) {
      newErrors.email = 'Correo con formato inválido'
    }
    
    if (!firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido'
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = 'Los apellidos son requeridos'
    }
    
    if (!country.trim()) {
      newErrors.country = 'El país es requerido'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      setAlert({
        message: 'Por favor corrige los errores en el formulario',
        msgType: 'warning'
      })
      return
    }

    setIsLoading(true)

    try {
      let record = await patchById({ 
        path: 'users',
        id: authenticatedUser.userId,
        data: {
          userEmail: userEmail.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          country: country.trim(),
        }, 
        token: authenticatedUser.value 
      })

      if(Object.prototype.hasOwnProperty.call(record, 'userId')) {
        setAlert({
          message: 'Datos actualizados correctamente',
          msgType: 'success'
        })
        navigate('/home')
      } else {
        setAlert({
          message: record.message || 'Se produjo un error en el servidor',
          msgType: 'warning'
        })
      }
    } catch (error) {
      setAlert({
        message: 'Error al actualizar los datos',
        msgType: 'warning'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const fetchData = async () => {
    try {
      const user = await getById({ 
        path: 'users', 
        id: authenticatedUser.userId, 
        token: authenticatedUser.value 
      })
      setEmail(user.userEmail || '')
      setFirstName(user.firstName || '')
      setLastName(user.lastName || '')
      setCountry(user.country || '')
    } catch (error) {
      setAlert({
        message: 'Error al cargar los datos del usuario',
        msgType: 'warning'
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <div className="personal-info-container">
      <div className="container">
        <Row className="justify-content-center">
          <Col lg={8} md={10} sm={12}>
            {/* Header */}
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <FaUserEdit />
                </div>
                <div className="header-text">
                  <h1>Información personal</h1>
                  <p>Actualiza tus datos personales y de contacto</p>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <Card className="form-card">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    {/* Email Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaEnvelope className="field-icon" />
                          Correo Electrónico *
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="tu@email.com"
                          value={userEmail}
                          onChange={handleChangeEmail}
                          className={`form-control-custom ${errors.email ? 'is-invalid' : ''}`}
                        />
                        {errors.email && (
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>

                    {/* First Name Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaUser className="field-icon" />
                          Nombre *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Tu nombre"
                          value={firstName}
                          onChange={handleChangeFirstName}
                          className={`form-control-custom ${errors.firstName ? 'is-invalid' : ''}`}
                        />
                        {errors.firstName && (
                          <Form.Control.Feedback type="invalid">
                            {errors.firstName}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Last Name Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaUser className="field-icon" />
                          Apellidos *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Tus apellidos"
                          value={lastName}
                          onChange={handleChangeLastName}
                          className={`form-control-custom ${errors.lastName ? 'is-invalid' : ''}`}
                        />
                        {errors.lastName && (
                          <Form.Control.Feedback type="invalid">
                            {errors.lastName}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Country Field */}
                    <Col lg={6} md={12} className="mb-4">
                      <Form.Group>
                        <Form.Label className="form-label">
                          <FaGlobe className="field-icon" />
                          País de Residencia *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Tu país"
                          value={country}
                          onChange={handleChangeCountry}
                          className={`form-control-custom ${errors.country ? 'is-invalid' : ''}`}
                        />
                        {errors.country && (
                          <Form.Control.Feedback type="invalid">
                            {errors.country}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Action Buttons */}
                  <div className="form-actions">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="cancel-button"
                      onClick={() => navigate('/home')}
                      disabled={isLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="save-button"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Guardando...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
